import { AlertTriangle, RefreshCw } from 'lucide-react'
import React from 'react'
import Loader from '@/components/Loader'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useServerHealthCheck } from '@/hooks'

interface ServerHealthProviderProps {
  children: React.ReactNode
}

export function ServerHealthProvider({ children }: ServerHealthProviderProps) {
  const { isServerReady, isChecking, error, retryCount, retryHealthCheck } =
    useServerHealthCheck()

  // Show loader while checking server health
  if (isChecking) {
    return (
      <div className="fixed inset-0 w-screen h-screen flex flex-col items-center justify-center bg-background z-50">
        <Loader />
        <div className="mt-8 text-center space-y-2">
          <p className="text-sm text-muted-foreground">
            Connecting to server...
          </p>
          {retryCount > 0 && (
            <p className="text-xs text-muted-foreground">
              Attempt {retryCount} of {import.meta.env.DEV ? 15 : 10}
            </p>
          )}
        </div>
      </div>
    )
  }

  // Show error state if server is not ready after all retries
  if (!isServerReady && error) {
    return (
      <div className="fixed inset-0 w-screen h-screen flex items-center justify-center bg-background p-4 z-50">
        <Card className="w-full max-w-md border-red-200 bg-red-50">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <CardTitle className="text-xl font-bold text-red-900">
              Server Not Available
            </CardTitle>
            <CardDescription className="text-red-700">{error}</CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="text-sm text-red-800 space-y-2">
              <p>This could happen if:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>The server is still starting up</li>
                <li>The server is sleeping due to inactivity</li>
                <li>There's a network connectivity issue</li>
              </ul>
            </div>

            <div className="flex flex-col gap-3">
              <Button onClick={retryHealthCheck} className="w-full">
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>

              {import.meta.env.DEV && (
                <div className="text-xs text-red-600 bg-red-100 p-3 rounded border">
                  <strong>Development tip:</strong> Make sure to run{' '}
                  <code>npm run dev</code> in the server directory and verify
                  your .env file has the required MongoDB URI and other
                  settings.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Server is ready, render the app
  return <>{children}</>
}
